import { BTPopover } from "@btnet/core-components/BTPopover";

import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { isInPortal } from "utilities/portal/portal";

import { BTFilterButton } from "commonComponents/btWrappers/BTFilterButton/BTFilterButton";
import { JobStatusFilterTypes } from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import { JobPickerFilterTypes } from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import { type JobSortOptions } from "commonComponents/utilities/JobPicker/JobPicker.types";

import {
    type FilterEntity,
    FilterEntityType,
    type IFilterFormValues,
    type SelectedFilterItem,
} from "entity/filters/Filter/Filter.api.types";
import { getAppliedFilterCount } from "entity/filters/filters.utils";

import "./JobPickerFiltersPopover.less";

/**
 * Loaded on demand: the form renders only inside the popover, which stays closed until the filter
 * button is clicked, and it pulls in the saved-views feature behind it.
 */
const FilterUnmanaged = lazyLoadComponent(() =>
    import("entity/filters/Filter/Filter").then((m) => ({ default: m.FilterUnmanaged })),
);

// The "Saved Filters" BTModal renders at z-index 100 (BTModal's default). When it is
// open the filter popover stays mounted, so drop it below the modal; otherwise it would
// paint over the modal at its usual top-most z-index.
const BEHIND_SAVED_FILTERS_MODAL_ZINDEX = 50;

interface IJobPickerFiltersPopoverProps {
    showPopover: boolean;
    sortOption: JobSortOptions;
    onVisibleChange: (visible: boolean) => void;
    shouldCloseOnScroll?: boolean;
    templatesOnly: boolean;
    selectedFilters: SelectedFilterItem[] | undefined;
    filters: FilterEntity;
    selectedFilterId?: number;
    areSavedFiltersShowing: boolean;
    onFilterSubmit: (filterValues: IFilterFormValues, keyword: string) => Promise<void>;
    onSavedFiltersUpdated: (filters: FilterEntity) => void;
    onSavedFilterVisibleChange?: (visible: boolean) => void;
}

function handleDisableFilterField(
    filterField: SelectedFilterItem,
    filterValues: SelectedFilterItem[],
) {
    if ((filterField.key as JobPickerFilterTypes) !== JobPickerFilterTypes.LeadStatus) {
        return false;
    }
    const jobStatusFilter = filterValues.find(
        (x) => (x.key as JobPickerFilterTypes) === JobPickerFilterTypes.Other,
    );
    if (!jobStatusFilter) {
        return false;
    }
    const selectedValue = jobStatusFilter.selectedValue as JobStatusFilterTypes[];
    return selectedValue.length > 0 && !selectedValue.includes(JobStatusFilterTypes.Presale);
}

export function JobPickerFiltersPopover(props: IJobPickerFiltersPopoverProps) {
    const filterType = props.templatesOnly
        ? FilterEntityType.TemplateJobPicker
        : FilterEntityType.JobPicker;

    const appliedFilterCount = getAppliedFilterCount(props.selectedFilters ?? [], props.filters);

    const popoverContent = (
        <div className="py-2 px-3">
            <FilterUnmanaged
                filterId={0}
                entity={props.filters}
                selectedFilterId={props.selectedFilterId}
                appliedFilter={props.selectedFilters}
                jobPickerFilterOverride={props.selectedFilters}
                filterTypeEntity={filterType}
                onSubmit={async (values) => {
                    props.onVisibleChange(false);
                    await props.onFilterSubmit(values, "");
                }}
                onSavedFiltersUpdated={(entity) => {
                    props.onSavedFiltersUpdated(entity);
                }}
                isInPopup
                showSaveButton={isInPortal({ builder: true })}
                showSavedFilters={isInPortal({ builder: true })}
                onSavedFilterVisibleChange={props.onSavedFilterVisibleChange}
                showHasUnsavedChanges={false}
                appliedFiltersFromSavedFilter
                shouldUpdateFilterValueContext={false}
                handleDisableFilterField={handleDisableFilterField}
            />
        </div>
    );

    return (
        <BTPopover
            className="ActionIcon"
            content={popoverContent}
            placement="rightTop"
            trigger="click"
            shouldCloseOnScroll={props.shouldCloseOnScroll}
            overlayClassName="JobPickerFilterPopoverOverlay"
            zIndex={props.areSavedFiltersShowing ? BEHIND_SAVED_FILTERS_MODAL_ZINDEX : undefined}
            visible={props.showPopover || props.areSavedFiltersShowing}
            // Bringing up the confirm modal hides this popover; prevent that to keep things
            // linked while saved-filter flow is active.
            onVisibleChange={(visible) =>
                !props.areSavedFiltersShowing && props.onVisibleChange(visible)
            }
        >
            <BTFilterButton filterCount={appliedFilterCount} data-testid="jobPickerFilter" />
        </BTPopover>
    );
}
